import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  Paper,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  LinearProgress,
  Chip,
  Divider,
  Alert,
  IconButton,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import katex from "katex";
import "katex/dist/katex.min.css";
import sanitizeHtml from "sanitize-html";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useTheme } from '@mui/material/styles';

const StudentPracticePage = () => {
      const theme = useTheme();
  const [showImage, setShowImage] = useState({});
  const navigate = useNavigate();
  const { quizId } = useParams();
  const [questions, setQuestions] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [confirmSubmitModalOpen, setConfirmSubmitModalOpen] = useState(false);
  const [unansweredCount, setUnansweredCount] = useState(0);
  const [quizResult, setQuizResult] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [quizHistory, setQuizHistory] = useState([]);
  const [quizTitle, setQuizTitle] = useState("");
  const [timeLimit, setTimeLimit] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerWarning, setTimerWarning] = useState(false);
  const [skippedQuestions, setSkippedQuestions] = useState([]);
  const [markedForReview, setMarkedForReview] = useState([]);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = sessionStorage.getItem("student_token");
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [showTabWarning, setShowTabWarning] = useState(false);
  const [quizLocked, setQuizLocked] = useState(false);
  const [zoomImage, setZoomImage] = useState(null);
  const [zoomModalOpen, setZoomModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const renderContent = (html) => {
  if (!html) return null;
  const parts = html.split(/(\$\$.+?\$\$|\$.+?\$)/g);
  return (
    <div>
      {parts.map((part, index) => {
        if (part.startsWith("$$") && part.endsWith("$$")) {
          const latex = part.slice(2, -2);
          try {
            const renderedLatex = katex.renderToString(latex, { displayMode: true });
            return (
              <div
                key={index}
                dangerouslySetInnerHTML={{ __html: renderedLatex }}
                className="rendered-content block-latex"
              />
            );
          } catch (error) {
            console.error("Block LaTeX rendering error:", error);
            return <div key={index} className="error-content">{part}</div>;
          }
        }
        if (part.startsWith("$") && part.endsWith("$")) {
          const latex = part.slice(1, -1);
          try {
            const renderedLatex = katex.renderToString(latex, { displayMode: false });
            return (
              <span
                key={index}
                dangerouslySetInnerHTML={{ __html: renderedLatex }}
                className="rendered-content inline-latex"
              />
            );
          } catch (error) {
            console.error("Inline LaTeX rendering error:", error);
            return <span key={index} className="error-content">{part}</span>;
          }
        }
        try {
          const formattedContent = part.replace(/\n/g, "<br />");
          const sanitizedContent = sanitizeHtml(formattedContent, {
            allowedTags: ["b", "i", "em", "strong", "span", "br", "u", "sub", "sup", "a", "img"],
            allowedAttributes: {
              img: ["src", "alt"],
              a: ["href", "target"],
              span: ["style"],
            },
            transformTags: {
              p: "span",
              div: "span",
            },
          });
          return (
            <span
              key={index}
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
              className="rendered-content inline-text"
            />
          );
        } catch (error) {
          console.error("HTML rendering error:", error);
          return <span key={index} className="error-content">{part}</span>;
        }
      })}
    </div>
  );
};

  // Detect tab switching and warn user
  useEffect(() => {
    if (submitted || loading || quizLocked) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // User switched tab/minimized window
        setTabSwitchCount((prev) => {
          const newCount = prev + 1;

          // Log the tab switch (you can send this to backend)
          console.warn(`Tab switch detected! Count: ${newCount}`);

          // Show warning
          setShowTabWarning(true);

          // Lock quiz after 3 tab switches
          if (newCount >= 3) {
            setQuizLocked(true);
            // alert("Quiz locked due to multiple tab switches. Your answers will be auto-submitted.");
            setSnackbarMessage("Quiz locked due to multiple tab switches. Your answers will be auto-submitted.");
            setSnackbarOpen(true);
            setSnackbarSeverity("error");
            // Add a 2-second delay before auto-submitting
            setTimeout(() => {
              handleAutoSubmit();
            }, 2000);
          }

          return newCount;
        });
      }
    };

    const handleBlur = () => {
      // Additional detection when window loses focus
      if (!document.hidden) {
        console.warn("Window focus lost");
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  }, [submitted, loading, quizLocked]);

  // Prevent right-click and common keyboard shortcuts
  useEffect(() => {
    if (submitted || loading) return;

    const preventCheating = (e) => {
      // Prevent right-click
      if (e.type === 'contextmenu') {
        e.preventDefault();
        // alert("Right-click is disabled during the quiz.");
        setSnackbarMessage("Right-click is disabled during the quiz.");
        setSnackbarOpen(true);
        setSnackbarSeverity("error");
        return false;
      }

      // Prevent common shortcuts (Ctrl+C, Ctrl+V, F12, etc.)
      if (
        (e.ctrlKey && (e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 'u')) ||
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C'))
      ) {
        e.preventDefault();
        // alert("This action is disabled during the quiz.");
        setSnackbarMessage("This action is disabled during the quiz.");
        setSnackbarOpen(true);
        setSnackbarSeverity("error");
        return false;
      }
    };

    document.addEventListener('contextmenu', preventCheating);
    document.addEventListener('keydown', preventCheating);

    return () => {
      document.removeEventListener('contextmenu', preventCheating);
      document.removeEventListener('keydown', preventCheating);
    };
  }, [submitted, loading]);

  useEffect(() => {
    if (!token) navigate("/login");
  }, [token, navigate]);

  // Fetch questions and quiz metadata
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/quiz/${quizId}/questions/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data;
        setQuizTitle(data.title);
        setTimeLimit(data.time_limit_minutes);
        setTimeRemaining(data.time_limit_minutes * 60);
        const normalizedQuestions = data.questions.map((q) => ({
          ...q,
          answers: q.answers || (q.question_type?.toLowerCase() === "truefalse"
            ? [{ answer_text: "True" }, { answer_text: "False" }]
            : []),
        }));
        setQuestions(normalizedQuestions);
      } catch (err) {
        console.error("Error fetching questions:", err);
        // alert("Failed to load questions. Please try again.");
        setSnackbarMessage("Failed to load questions. Please try again.");
        setSnackbarOpen(true);
        setSnackbarSeverity("warning");
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [quizId, API_BASE_URL, token]);

  // Timer countdown
  useEffect(() => {
    if (submitted || loading || timeRemaining <= 0) return;
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleAutoSubmit();
          return 0;
        }
        if (prev <= 300 && !timerWarning) {
          setTimerWarning(true);
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timeRemaining, submitted, loading]);

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const openZoomModal = (imageUrl) => {
    setZoomImage(imageUrl);
    setZoomModalOpen(true);
  };

  const closeZoomModal = () => {
    setZoomModalOpen(false);
  };

  const toggleImageVisibility = (questionId) => {
    setShowImage((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };
  // Handle answer selection
  const handleAnswerChange = (questionId, answer, multiSelect = false, questionType) => {
    const key = `${questionId}-${questionType?.toUpperCase()}`;
    setUserAnswers((prev) => {
      const current = prev[key] || [];
      const updated = multiSelect
        ? current.includes(answer)
          ? current.filter((a) => a !== answer)
          : [...current, answer]
        : [answer];
      return { ...prev, [key]: updated };
    });
     setSkippedQuestions((prev) => prev.filter((id) => id !== questionId));
  };

  // Mark question for review
  const handleMarkForReview = (questionId) => {
    const currentQuestion = questions[currentQuestionIndex];
    const key = `${currentQuestion.id}-${currentQuestion.question_type?.toUpperCase()}`;
    if (!userAnswers[key]) {
      setUserAnswers((prev) => ({
        ...prev,
        [key]: [""],
      }));
    }
    setMarkedForReview((prev) => [...prev, questionId]);
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handleExportReview = async (quizId, attemptId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/student/export-quiz-review/${quizId}/${attemptId}/`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob", // Important for file downloads
      }
    );

    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Quiz_Review_${reviewData?.title || "Quiz"}_Attempt_${attemptId}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  } catch (err) {
    console.error("Error exporting quiz review:", err);
    setSnackbarMessage("Failed to export quiz review.");
    setSnackbarOpen(true);
    setSnackbarSeverity("error");
  }
};


  // Check for unanswered questions and show confirmation modal
  const handleSubmitClick = () => {
    const unansweredQuestions = questions.filter((q) => {
      const key = `${q.id}-${q.question_type?.toUpperCase()}`;
      return (
        !skippedQuestions.includes(q.id) &&
        !markedForReview.includes(q.id) &&
        !userAnswers[key]
      );
    });
    if (unansweredQuestions.length > 0) {
      setUnansweredCount(unansweredQuestions.length);
      setConfirmSubmitModalOpen(true);
    } else {
      handleSubmit(false);
    }
  };

  // Confirm submission from modal
  const handleConfirmSubmit = () => {
    setConfirmSubmitModalOpen(false);
    handleSubmit(false);
  };

  // Auto submit when time runs out
  const handleAutoSubmit = async () => {
    await handleSubmit(true);
  };

  // Submit quiz
  const handleSubmit = async (autoSubmit = false) => {
    const answersArray = questions
      .filter((q) => {
        const key = `${q.id}-${q.question_type?.toUpperCase()}`;
        return (
          // !skippedQuestions.includes(q.id) &&
          !markedForReview.includes(q.id) &&
          userAnswers[key] !== undefined &&
          userAnswers[key].length > 0
        );
      })
      .map((q) => {
        const key = `${q.id}-${q.question_type?.toUpperCase()}`;
        const userAnswer = userAnswers[key];
        let selected_answer = userAnswer || "";
        let question_type = q.question_type?.toUpperCase();
        if (question_type === "TRUEFALSE") question_type = "TF";
        if (question_type === "FIB") question_type = "FILL";
        if (Array.isArray(userAnswer)) {
          if (question_type === "MCQ" || question_type === "FILL") {
            selected_answer = userAnswer;
          } else if (question_type === "IMAGE") {
            selected_answer = {};
            userAnswer.forEach((ans, index) => {
              selected_answer[index + 1] = ans;
            });
          } else {
            selected_answer = userAnswer[0] || "";
          }
        }
        return {
          question_id: q.id,
          question_type,
          selected_answer,
        };
      });
    try {
      setSubmitting(true);
      // Inside handleSubmit function, modify the axios.post to include:
      const submitRes = await axios.post(
        `${API_BASE_URL}/quiz/${quizId}/submit/`,
        {
          answers: answersArray,
          tab_switch_count: tabSwitchCount,
          forced_submission: autoSubmit || quizLocked
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuizResult(submitRes.data);

      setSubmitted(true);
      if (autoSubmit) {
        // alert("Time's up! Quiz has been automatically submitted.");
        setSnackbarMessage("Quiz has been automatically submitted.");
        setSnackbarOpen(true);
        setSnackbarSeverity("error");
      }
    } catch (err) {
      console.error("Error submitting quiz:", err);
      // alert("Failed to submit quiz.");
      setSnackbarMessage("Failed to submit quiz");
      setSnackbarOpen(true);
      setSnackbarSeverity("error");
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch quiz history
  const fetchQuizHistory = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/quiz-history/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuizHistory(res.data.history || []);
      setHistoryModalOpen(true);
    } catch (err) {
      console.error("Error fetching quiz history:", err);
      // alert("Failed to load history.");
      setSnackbarMessage("Failed to load history.");
      setSnackbarOpen(true);
      setSnackbarSeverity("warning");
    }
  };

  // Fetch review for specific attempt
  const fetchReviewQA = async (attemptId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/quiz/${quizId}/review/${attemptId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviewData(res.data);
      setReviewModalOpen(true);
    } catch (err) {
      console.error("Error fetching review:", err);
      // alert("Failed to fetch review.");
      setSnackbarMessage("Failed to fetch review.");
      setSnackbarOpen(true);
      setSnackbarSeverity("warning");
    }
  };
  const parseGivenAnswer = (givenAnswer) => {
    if (!givenAnswer) return "Not Attempted";
    // Remove the square brackets and quotes
    const cleaned = givenAnswer.replace(/[\[\]']+/g, '');
    return cleaned || "Not Attempted";
  };

  const getExplanationForAnswer = (question) => {
    const parsedAnswer = parseGivenAnswer(question.given_answer);
    if (!parsedAnswer || parsedAnswer === "Not Attempted") return null;

    // For correct answers, find the correct answer's explanation
    if (question.is_correct) {
      const correctAnswerObj = question.answers.find(ans => ans.is_correct === true);
      return correctAnswerObj?.explanation || null;
    }

    // For incorrect answers, find the explanation of what they selected
    const matchedAnswer = question.answers.find(
      (ans) => ans.answer_text.toLowerCase().trim() === parsedAnswer.toLowerCase().trim()
    );
    return matchedAnswer?.explanation || null;
  };
  // Calculate progress percentage
  const calculateProgress = () => {
    const answeredCount = Object.keys(userAnswers).length;
    return (answeredCount / questions.length) * 100;
  };

  // Check if answer is correct
  const isAnswerCorrect = (givenAnswer, correctAnswer) => {
    if (!givenAnswer || givenAnswer === "Not Attempted") return false;
    const given = String(givenAnswer).trim().toLowerCase();
    const correct = String(correctAnswer).trim().toLowerCase();
    return given === correct;
  };

  // Render question card
  const renderQuestionCard = (question, index) => {
    const questionType = question.question_type?.toLowerCase();
    const answers = question.answers || [];
    const key = `${question.id}-${questionType?.toUpperCase()}`;

    return (
      <Paper
        sx={{
          p: 4,
          mb: 3,
          position: "relative",
          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.1)",
          borderRadius: "16px",
          border: "1px solid #e0e7ff",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
          },
        }}
        key={question.id}
      >
        <Typography
          variant="body2"
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            fontWeight: "bold",
            color: "#667eea",
            background: "rgba(102, 126, 234, 0.1)",
            padding: "4px 12px",
            borderRadius: "12px",
            fontSize: "0.85rem",
          }}
        >
          {questionType === "mcq"
            ? "MCQ (Multiple Choice)"
            : questionType === "truefalse"
              ? "TRUE / FALSE"
              : questionType === "fib"
                ? "FILL IN THE BLANK"
                : "SUBJECTIVE"}
        </Typography>
        {questionType !== "fib" && (
        <Box
  sx={{
    mt: 4,
    mb: 3,
  }}
>
  <Typography
    component="span"
    variant="h5"
    sx={{
      fontWeight: "700",
      color: theme.palette.text.primary,
      lineHeight: 1.6,
    }}
  >
    {index + 1}.{" "}
  </Typography>
  <Typography
    component="span"
    variant="h5"
    sx={{
      fontWeight: "700",
      color: theme.palette.text.primary,
      lineHeight: 1.6,
      "& > div": {
        display: "inline",
      },
    }}
  >
    {renderContent(question.statement)}
  </Typography>
</Box>
        )}
        {questionType === "mcq" && (
          <Box>
            {/* Show/Hide Image Button */}
            {question.image && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => toggleImageVisibility(question.id)}
                sx={{
                  mb: 2,
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: "600",
                  borderColor: "#667eea",
                  color: "#667eea",
                  "&:hover": {
                    borderColor: "#667eea",
                    background: "rgba(102, 126, 234, 0.08)",
                  },
                }}
              >
                {showImage[question.id] ? "Hide Image" : "Show Image"}
              </Button>
            )}
            {/* Image (Conditionally Rendered) */}
            {showImage[question.id] && question.image && (
              <Box sx={{ mb: 2, textAlign: "center" }}>
                <img
                  src={`${API_BASE_URL}${question.image}`}
                  alt="Question"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "300px",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                    cursor: "zoom-in",
                  }}
                  onClick={() => openZoomModal(`${API_BASE_URL}${question.image}`)}
                />
              </Box>
            )}

            {/* MCQ Options */}
            <FormGroup>
              {answers.map((a, i) => (
                <FormControlLabel
                  key={i}
                  control={
                    <Checkbox
      checked={Array.isArray(userAnswers[key]) && userAnswers[key].includes(a.answer_text)}
      onChange={() => handleAnswerChange(question.id, a.answer_text, true, questionType?.toUpperCase())}
      sx={{ "&.Mui-checked": { color: "#667eea" } }}
    />
  }
  label={
    <Typography
      sx={{
        fontSize: "1rem",
        color: theme.palette.text.secondary,
        fontWeight: "500",
      }}
      component="div"
    >
      {renderContent(a.answer_text)}
    </Typography>
  }
/>
              ))}
            </FormGroup>
          </Box>
        )}

        {questionType === "truefalse" && (
          <RadioGroup
            value={userAnswers[key]?.[0] || ""}
            onChange={(e) => handleAnswerChange(question.id, e.target.value, false, questionType?.toUpperCase())}
          >
            {answers.map((a, i) => (
              <FormControlLabel
                key={i}
                value={a.answer_text}
                control={<Radio sx={{ "&.Mui-checked": { color: "#667eea" } }} />}
                label={
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      color: theme.palette.text.secondary,
                      fontWeight: "500",
                    }}
                  >
                    {a.answer_text}
                  </Typography>
                }
              />
            ))}
          </RadioGroup>
        )}
       {questionType === "fib" && (
  <Box>
    {/* Display the question statement with a single blank */}
<Box
  sx={{
    mt: 4,
    mb: 3,
    display: "inline-block",
    width: "100%",
  }}
>
  <Typography
    variant="h5"
    sx={{
      fontWeight: "700",
      color: theme.palette.text.primary,
      lineHeight: 1.6,
      display: "inline",
      "& > span > div": {
        display: "inline",
      },
    }}
  >
    <span>{index + 1}. </span>
    {question.statement.split("[BLANK]").reduce((acc, part, blankIndex, arr) => {
      if (blankIndex === arr.length - 1) {
        return [...acc, <span key={`part-${blankIndex}`} style={{ display: "inline" }}>{renderContent(part)}</span>];
      } else {
        return [
          ...acc,
          <span key={`part-${blankIndex}`} style={{ display: "inline" }}>{renderContent(part)}</span>,
          <Box
            key={`blank-${blankIndex}`}
            component="span"
            sx={{
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
              borderBottom: "2px dashed #999",
              minWidth: "120px",
              height: "32px",
              mx: 0.5,
              verticalAlign: "middle",
            }}
          >
            {userAnswers[key]?.length > 0 ? (
              <Typography
                component="span"
                sx={{
                  color: "#667eea",
                  fontWeight: "bold",
                  fontStyle: "italic",
                  textAlign: "center",
                  lineHeight: "32px",
                }}
              >
                {renderContent(userAnswers[key].join(", "))}
              </Typography>
            ) : (
              <Typography
                component="span"
                sx={{
                  color: "#999",
                  fontStyle: "italic",
                  textAlign: "center",
                  lineHeight: "32px",
                }}
              >
                      
              </Typography>
            )}
          </Box>,
        ];
      }
    }, [])}
  </Typography>
</Box>

    {/* Options as checkboxes */}
    <FormGroup>
      {answers.map((a, i) => (
        <FormControlLabel
          key={i}
          control={
            <Checkbox
      checked={Array.isArray(userAnswers[key]) && userAnswers[key].includes(a.answer)}
      onChange={() => handleAnswerChange(question.id, a.answer, true, questionType?.toUpperCase())}
      sx={{ "&.Mui-checked": { color: "#667eea" } }}
    />
  }
  label={
    <Typography
      sx={{
        fontSize: "1rem",
        color: theme.palette.text.secondary,
        fontWeight: "500",
      }}
      component="div"
    >
      {renderContent(a.answer)}
    </Typography>
  }
/>
      ))}
    </FormGroup>
  </Box>
)}


     {questionType === "sub" && (
  <Box sx={{ position: "relative" }}>
    <ReactQuill
      value={userAnswers[key]?.[0] || ""}
      onChange={(value) => handleAnswerChange(question.id, value, false, questionType?.toUpperCase())}
      placeholder="Type your answer here..."
      modules={{
        toolbar: [
          // ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ script: "sub" }, { script: "super" }],
          ["clean"],
        ],
      }}
      formats={[
        // "bold", "italic", "underline", "strike",
        "list", "bullet",
        "script",
      ]}
      style={{
        borderRadius: "12px",
        border: "2px solid #e0e7ff",
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        minHeight: "150px",
        marginBottom: "20px",
      }}
    />
  </Box>
)}

      </Paper>
    );
  };

  // UI
  if (loading)
    return (
      <Box
        sx={{
          textAlign: "center",
          mt: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: "#667eea" }} />
        <Typography
          variant="h6"
          mt={2}
          sx={{ color: "#6b7280", fontWeight: "600" }}
        >
          Loading questions...
        </Typography>
      </Box>
    );

  if (questions.length === 0)
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography
          variant="h6"
          sx={{ color: "#6b7280", fontWeight: "600" }}
        >
          No questions available for this quiz.
        </Typography>
      </Box>
    );

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        // maxWidth: 1200,
        mx: "auto",
        minHeight: "100vh",
        // background: "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
        borderRadius: "24px",
        // boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
      }}
    >
     <style>
  {`
    ${theme.palette.mode === 'dark' ? `
      .ql-toolbar .ql-stroke {
        stroke: #e5e7eb !important;
      }
      .ql-toolbar .ql-fill {
        fill: #e5e7eb !important;
      }
      .ql-toolbar .ql-picker-label {
        color: #e5e7eb !important;
      }
      .ql-toolbar button:hover .ql-stroke,
      .ql-toolbar button:focus .ql-stroke {
        stroke: #667eea !important;
      }
      .ql-toolbar button:hover .ql-fill,
      .ql-toolbar button:focus .ql-fill {
        fill: #667eea !important;
      }
      .ql-editor.ql-blank::before {
        color: #9ca3af !important;
      }
      .ql-editor {
        color: #e5e7eb !important;
      }
      .ql-toolbar.ql-snow,
      .ql-container.ql-snow {
        border-color: rgba(255, 255, 255, 0.12) !important;
      }
    ` : ''}
  `}
</style>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        {!submitted && (
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: "800",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {quizTitle}
          </Typography>
        )}
        {/* Timer and Progress Bar */}
        {!submitted && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 3,
                p: 2,
                bgcolor: timerWarning ? "#fff3e0" : "#e3f2fd",
                borderRadius: "16px",
                border: timerWarning ? "2px solid #ff9800" : "2px solid #2196f3",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              }}
            >
              <AccessTimeIcon
                sx={{
                  color: timerWarning ? "#ff9800" : "#2196f3",
                  fontSize: "2rem",
                }}
              />
              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant="body2"
                  color="#403f3fff"
                  sx={{ fontWeight: "600" }}
                >
                  Time Remaining
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    color: timerWarning ? "#f57c00" : "#1976d2",
                  }}
                >
                  {formatTime(timeRemaining)}
                </Typography>
              </Box>
              <Chip
                label={`Question ${currentQuestionIndex + 1} of ${questions.length}`}
                color="primary"
                variant="outlined"
                sx={{
                  fontWeight: "600",
                  fontSize: "0.9rem",
                  borderRadius: "8px",
                }}
              />
            </Box>
            {timerWarning && (
              <Alert
                severity="warning"
                sx={{
                  mb: 2,
                  borderRadius: "12px",
                  "& .MuiAlert-icon": {
                    color: "#f57c00",
                  },
                }}
              >
                Less than 5 minutes remaining! Please review your answers.
              </Alert>
            )}
            {showTabWarning && tabSwitchCount < 3 && (
              <Alert
                severity="error"
                onClose={() => setShowTabWarning(false)}
                sx={{
                  mb: 2,
                  borderRadius: "12px",
                  "& .MuiAlert-icon": {
                    color: "#d32f2f",
                  },
                }}
              >
                <strong>Warning!</strong> Tab switching detected ({tabSwitchCount}/3).
                After 3 switches, your quiz will be auto-submitted.
              </Alert>
            )}

            {quizLocked && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  borderRadius: "12px",
                  "& .MuiAlert-icon": {
                    color: "#d32f2f",
                  },
                }}
              >
                <strong>Quiz Locked!</strong> Too many tab switches detected.
                Your quiz has been auto-submitted.
              </Alert>
            )}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: "600" }}
                >
                  Progress: {Object.keys(userAnswers).length} of {questions.length}{" "}
                  answered
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: "600" }}
                >
                  {Math.round(calculateProgress())}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={calculateProgress()}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    bgcolor: calculateProgress() === 100 ? "#4caf50" : "#667eea",
                  },
                }}
              />
            </Box>
          </>
        )}
      </Box>

      {/* Content */}
      {submitted ? (
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          {/* Success Header Card */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 1,
              background: (quizResult.neg_marking ? quizResult.result : quizResult.no_negative_result) === "Pass"
                ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              borderRadius: "20px",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "50%",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: -30,
                left: -30,
                width: 150,
                height: 150,
                background: "rgba(255, 255, 255, 0.08)",
                borderRadius: "50%",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 1,
                position: "relative",
                zIndex: 1,
              }}
            >
              <CheckCircleIcon
                sx={{
                  fontSize: 50,
                  color: "white",
                  filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.2))",
                  animation: "pulse 2s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { transform: "scale(1)" },
                    "50%": { transform: "scale(1.05)" },
                  },
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  color: "white",
                  fontWeight: "700",
                }}
              >
                Quiz Submitted Successfully!
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "1.0rem",
                position: "relative",
                zIndex: 1,
              }}
            >
              Your answers have been recorded
            </Typography>
          </Paper>

          {/* Quiz Details Card */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: "20px",
              border: "1px solid #e0e7ff",
              background: theme.palette.background.paper,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
            }}
          >


            {/* Score and Result Section */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-around",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 4,
              }}
            >
              {/* Score Circle */}
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 300,
                    height: 300,
                    borderRadius: "50%",
                    background: `conic-gradient(
                ${(quizResult.neg_marking ? quizResult.result : quizResult.no_negative_result) === "Pass"
                        ? "#10b981"
                        : "#f59e0b"} ${quizResult.neg_marking
                          ? (quizResult.final_score / quizResult.total_questions) * 360
                          : (quizResult.correct_answers / quizResult.total_questions) * 360}deg,
                #e0e7ff 0deg
              )`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    boxShadow: "0 8px 30px rgba(102, 126, 234, 0.3)",
                  }}
                >
                  <Box
                    sx={{
                      width: 270,
                      height: 270,
                      borderRadius: "50%",
                      bgcolor: theme.palette.background.paper,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: "800",
                        background: (quizResult.neg_marking ? quizResult.result : quizResult.no_negative_result) === "Pass"
                          ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                          : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        lineHeight: 1,
                      }}
                    >
                      {quizResult.neg_marking
                        ? quizResult.final_score
                        : quizResult.correct_answers}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6b7280",
                        fontWeight: "600",
                        mt: 0.5,
                      }}
                    >
                      / {quizResult.total_questions}
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    mt: 2,
                    fontWeight: "700",
                    color: "#1f2937",
                  }}
                >
                  Your Score
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#6b7280",
                    fontWeight: "600",
                    fontSize: "1rem",
                  }}
                >
                  {Math.round(quizResult.neg_marking
                    ? quizResult.score_percentage
                    : quizResult.no_negative_marking_score_percentage)}% Score
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#9ca3af",
                      fontWeight: "500",
                    }}
                  >
                    Passing:
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#667eea",
                      fontWeight: "600",
                    }}
                  >
                    {quizResult.passing_percentage}%
                  </Typography>
                </Box>
              </Box>

              {/* Result Status Card */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  flex: 1,
                  minWidth: 280,
                  maxWidth: 700,
                }}
              >
                {/* Result Badge */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    background: (quizResult.neg_marking ? quizResult.result : quizResult.no_negative_result) === "Pass"
                      ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                      : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    textAlign: "center",
                    boxShadow: (quizResult.neg_marking ? quizResult.result : quizResult.no_negative_result) === "Pass"
                      ? "0 8px 25px rgba(16, 185, 129, 0.3)"
                      : "0 8px 25px rgba(239, 68, 68, 0.3)",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      color: "white",
                      fontWeight: "700",
                      mb: 1,
                    }}
                  >
                    {(quizResult.neg_marking ? quizResult.result : quizResult.no_negative_result) === "Pass" ? "🎉 Passed!" : "Failed!"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                    }}
                  >
                    {(quizResult.neg_marking ? quizResult.result : quizResult.no_negative_result) === "Pass"
                      ? "Congratulations on your success!"
                      : "Review your answers and try again"}
                  </Typography>
                </Paper>

                {/* Stats Grid */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 2,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "12px",
                      bgcolor: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                      textAlign: "center",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 6px 20px rgba(16, 185, 129, 0.2)",
                      },
                    }}
                  >
                    <CheckCircleIcon sx={{ color: "#10b981", fontSize: 32, mb: 1 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "700",
                        color: "#059669",
                      }}
                    >
                      {quizResult.correct_answers}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#047857",
                        fontWeight: "600",
                      }}
                    >
                      Correct
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "12px",
                      bgcolor: "#fef2f2",
                      border: "1px solid #fecaca",
                      textAlign: "center",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 6px 20px rgba(239, 68, 68, 0.2)",
                      },
                    }}
                  >
                    <CancelIcon sx={{ color: "#ef4444", fontSize: 32, mb: 1 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "700",
                        color: "#dc2626",
                      }}
                    >
                      {quizResult.wrong_answers}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#b91c1c",
                        fontWeight: "600",
                      }}
                    >
                      Incorrect
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "12px",
                      bgcolor: "#ede9fe",
                      border: "1px solid #ddd6fe",
                      textAlign: "center",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 6px 20px rgba(139, 92, 246, 0.2)",
                      },
                    }}
                  >
                    <InfoOutlinedIcon sx={{ color: "#8b5cf6", fontSize: 32, mb: 1 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "700",
                        color: "#7c3aed",
                      }}
                    >
                      {quizResult.neg_marking ? "Yes" : "No"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#6d28d9",
                        fontWeight: "600",
                      }}
                    >
                      Negative Marking
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "12px",
                      bgcolor: "#fef3c7",
                      border: "1px solid #fde68a",
                      textAlign: "center",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 6px 20px rgba(217, 119, 6, 0.2)",
                      },
                    }}
                  >
                    <AccessTimeIcon sx={{ color: "#d97706", fontSize: 32, mb: 1 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "700",
                        color: "#d97706",
                      }}
                    >
                      {quizResult.attempted_questions} / {quizResult.total_questions}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#b45309",
                        fontWeight: "600",
                      }}
                    >
                      Questions Attempted
                    </Typography>
                  </Paper>
                </Box>


                {/* Performance Indicator */}

              </Box>
            </Box>
          </Paper>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
              mb: 3,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => fetchReviewQA(quizResult.quiz_attempt_id)}
              startIcon={<InfoOutlinedIcon />}
              sx={{
                borderRadius: "12px",
                padding: "14px 36px",
                fontWeight: "600",
                textTransform: "none",
                borderWidth: "2px",
                borderColor: "#667eea",
                color: "#667eea",
                fontSize: "1rem",
                minWidth: 200,
                "&:hover": {
                  borderWidth: "2px",
                  borderColor: "#667eea",
                  background: "rgba(102, 126, 234, 0.08)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Review Answers
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate("/student-dashboard")}
              sx={{
                borderRadius: "12px",
                padding: "14px 36px",
                fontWeight: "600",
                textTransform: "none",
                fontSize: "1rem",
                minWidth: 200,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                boxShadow: "0 4px 20px rgba(102, 126, 234, 0.4)",
                "&:hover": {
                  background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 25px rgba(102, 126, 234, 0.5)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Back to Dashboard
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          {renderQuestionCard(questions[currentQuestionIndex], currentQuestionIndex)}

          {/* Navigation Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 4,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              disabled={currentQuestionIndex === 0}
              onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "10px 20px",
                fontWeight: "600",
                textTransform: "none",
                borderColor: "#667eea",
                color: "#667eea",
                "&:hover": {
                  borderColor: "#667eea",
                  background: "rgba(102, 126, 234, 0.08)",
                },
                "&.Mui-disabled": {
                  borderColor: "#e0e0e0",
                  color: "#9e9e9e",
                },
              }}
            >
              Previous
            </Button>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              {currentQuestionIndex < questions.length - 1 && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setSkippedQuestions((prev) => [...prev, questions[currentQuestionIndex].id]);
                    if (currentQuestionIndex < questions.length - 1) {
                      setCurrentQuestionIndex((prev) => prev + 1);
                    }
                  }}
                  sx={{
                    minWidth: 100,
                    borderRadius: "12px",
                    padding: "10px 20px",
                    fontWeight: "600",
                    textTransform: "none",
                    borderColor: "#ef4444",
                    color: "#ef4444",
                    "&:hover": {
                      borderColor: "#ef4444",
                      background: "rgba(239, 68, 68, 0.08)",
                    },
                  }}
                >
                  Skip
                </Button>
              )}
              {/* <Button
                variant="outlined"
                color="warning"
                onClick={() => handleMarkForReview(questions[currentQuestionIndex].id)}
                sx={{
                  minWidth: 140,
                  borderRadius: "12px",
                  padding: "10px 20px",
                  fontWeight: "600",
                  textTransform: "none",
                  borderColor: "#f59e0b",
                  color: "#f59e0b",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    background: "rgba(245, 158, 11, 0.08)",
                  },
                }}
              >
                Mark for Review
              </Button> */}
              <Button
                variant="contained"
                color="success"
                onClick={handleSubmitClick}
                disabled={submitting}
                sx={{
                  minWidth: 150,
                  borderRadius: "12px",
                  padding: "10px 24px",
                  fontWeight: "600",
                  textTransform: "none",
                  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                  },
                  "&.Mui-disabled": {
                    background: "#d1d5db",
                    color: "white",
                  },
                }}
              >
                {submitting ? "Submitting..." : "Submit Quiz"}
              </Button>
              {currentQuestionIndex < questions.length - 1 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setCurrentQuestionIndex((prev) => prev + 1)}
                  sx={{
                    minWidth: 140,
                    borderRadius: "12px",
                    padding: "10px 24px",
                    fontWeight: "600",
                    textTransform: "none",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                    },
                  }}
                >
                  Save and Next
                </Button>
              )}
            </Box>
          </Box>
        </>
      )}

      {/* Confirmation Modal for Submission */}
      <Modal open={confirmSubmitModalOpen} onClose={() => setConfirmSubmitModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 500 },
            bgcolor: "background.paper",
            borderRadius: "20px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 3,
              bgcolor: "#ff9800",
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Confirm Submission
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Please review before submitting
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 3 }}>
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                borderRadius: "12px",
                "& .MuiAlert-icon": {
                  color: "#f57c00",
                },
              }}
            >
              You have <strong>{unansweredCount}</strong> unanswered question(s).
            </Alert>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to submit the quiz? You won't be able to change your
              answers after submission.
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: theme.palette.background.default,
                borderRadius: "12px",
                border: "1px solid #e0e0e0",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>Quiz Summary:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Total Questions: {questions.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Answered: {Object.keys(userAnswers).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Unanswered: {unansweredCount}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.background.paper,
              borderTop: "1px solid #e0e0e0",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setConfirmSubmitModalOpen(false)}
              sx={{
                minWidth: 100,
                borderRadius: "12px",
                padding: "8px 20px",
                fontWeight: "600",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleConfirmSubmit}
              disabled={submitting}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "8px 24px",
                fontWeight: "600",
                textTransform: "none",
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                },
              }}
            >
              {submitting ? "Submitting..." : "Yes, Submit"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Quiz History Modal */}
      <Modal open={historyModalOpen} onClose={() => setHistoryModalOpen(false)}>
        <Box
          sx={{
            p: 4,
            bgcolor: "background.paper",
            mx: "auto",
            mt: "5%",
            width: "90%",
            maxWidth: 700,
            maxHeight: "80vh",
            overflowY: "auto",
            borderRadius: "20px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: "700", color: "#1f2937" }}
            >
              Quiz History
            </Typography>
            <IconButton onClick={() => setHistoryModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          {quizHistory.length > 0 ? (
            quizHistory.map((quiz, quizIdx) => (
              <Box key={quizIdx} sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: "bold",
                    mb: 1,
                    color: "#667eea",
                    borderBottom: "2px solid #e0e7ff",
                    pb: 1,
                  }}
                >
                  {quiz.title} (Attempts: {quiz.attempts.length}/{quiz.max_attempts})
                </Typography>
                {quiz.attempts.length > 0 ? (
                  quiz.attempts.map((attempt, attemptIdx) => (
                    <Paper
                      key={attemptIdx}
                      sx={{
                        p: 2,
                        mb: 1,
                        bgcolor: "#f5f5f5",
                        borderRadius: "12px",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <Typography>
                        <strong>Attempt #{attempt.attempt_number}</strong>
                      </Typography>
                      <Typography>Score: {attempt.final_score}</Typography>
                      <Typography>Result: {attempt.result}</Typography>
                      <Typography>Status: {attempt.quiz_status}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Started: {new Date(attempt.started_at).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ended: {new Date(attempt.ended_at).toLocaleString()}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          mt: 1,
                          borderRadius: "8px",
                          textTransform: "none",
                          fontWeight: "600",
                        }}
                        onClick={() => {
                          setHistoryModalOpen(false);
                          fetchReviewQA(attempt.quiz_attempt_id);
                        }}
                      >
                        Review QA
                      </Button>
                    </Paper>
                  ))
                ) : (
                  <Typography sx={{ color: "#6b7280" }}>No attempts yet.</Typography>
                )}
              </Box>
            ))
          ) : (
            <Typography sx={{ color: "#6b7280" }}>No history available</Typography>
          )}
        </Box>
      </Modal>

      {/* Enhanced Review Modal */}
      <Modal open={reviewModalOpen} onClose={() => setReviewModalOpen(false)}>
        <Box
          sx={{
            p: 0,
            bgcolor: "background.paper",
            mx: "auto",
            mt: "3%",
            mb: "3%",
            width: "90%",
            maxWidth: 900,
            maxHeight: "90vh",
            borderRadius: "20px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": {
      width: "10px",
      height: "10px",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9',
      borderRadius: "6px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.mode === 'dark' ? '#475569' : '#94a3b8',
      borderRadius: "6px",
      border: `2px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9'}`,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.mode === 'dark' ? '#64748b' : '#64748b',
    },
    // Custom scrollbar for Firefox
    scrollbarWidth: "thin",
    scrollbarColor: theme.palette.mode === 'dark' ? '#475569 #2d3748' : '#94a3b8 #f1f5f9',
  }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Review: {reviewData?.title || "Unknown Quiz"} (Attempt #{reviewData?.attempt_number || "N/A"})
              </Typography>
              {reviewData && (
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                  {reviewData.review?.length || 0} Questions
                </Typography>
              )}
            </Box>
       <Button
    variant="contained"
    onClick={async () => {
      await handleExportReview(reviewData?.quiz_id, reviewData?.attempt_id);
    }}
    startIcon={<i className="bi bi-download" style={{ fontSize: "1.2rem" }}></i>}
    sx={{
      background: "white",
      color: "#667eea",
      fontWeight: 700,
      borderRadius: "12px",
      px: 2,
      py: 1,
      textTransform: "none",
      boxShadow: "0 4px 15px rgba(139, 92, 246, 0.3)",
      "&:hover": {
        background: "#f5f5f5",
      },
    }}
  >
    Export
  </Button>
          </Box>
          {/* Modal Body */}
          <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
            {reviewData?.review?.length > 0 ? (
              reviewData.review.map((q, idx) => {
                const wasAttempted = q.given_answer && q.given_answer !== "Not Attempted";
                return (
                  <Paper
                    key={idx}
                    elevation={2}
                    sx={{
                      p: 3,
                      mb: 3,
                     border: q.is_correct
  ? "2px solid #4caf50"
  : wasAttempted
    ? "2px solid #f44336"
    : theme.palette.mode === 'dark' ? "2px solid #6b7280" : "2px solid #9e9e9e",
                      borderRadius: "16px",
                     bgcolor: q.is_correct
  ? theme.palette.mode === 'dark' ? '#1e3a2f' : "#f1f8f4"
  : wasAttempted
    ? theme.palette.mode === 'dark' ? '#451a03' : "#fef5f5"
    : theme.palette.mode === 'dark' ? '#2d3748' : "#fafafa",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {/* Question Header */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Chip
                            label={`Question ${idx + 1}`}
                            size="small"
                            sx={{
                              bgcolor: "#1976d2",
                              color: "white",
                              fontWeight: "bold",
                              borderRadius: "8px",
                            }}
                          />
                          <Chip
                            label={q.question_type}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: "#1976d2",
                              color: "#1976d2",
                              borderRadius: "8px",
                            }}
                          />
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: "600",
                            color: theme.palette.text.primary,
                            mt: 1,
                            lineHeight: 1.6,
                          }}
                        >
                        {renderContent(q.statement.split("[BLANK]").join("______"))}
                        </Typography>
                      </Box>
                      <Box>
                        {q.is_correct ? (
                          <CheckCircleIcon sx={{ fontSize: 40, color: "#4caf50" }} />
                        ) : wasAttempted ? (
                          <CancelIcon sx={{ fontSize: 40, color: "#f44336" }} />
                        ) : (
                          <InfoOutlinedIcon sx={{ fontSize: 40, color: "#9e9e9e" }} />
                        )}
                      </Box>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    {/* Question Body */}
                    <Box sx={{ mt: 2 }}>
                      {/* Your Answer */}
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          mb: 2,
                        bgcolor: wasAttempted
  ? q.is_correct
    ? theme.palette.mode === 'dark' ? '#1e3a2f' : "#e8f5e9"
    : theme.palette.mode === 'dark' ? '#451a03' : "#ffebee"
  : theme.palette.mode === 'dark' ? '#2d3748' : "#f5f5f5",
border: wasAttempted
  ? q.is_correct
    ? "1px solid #4caf50"
    : "1px solid #f44336"
  : theme.palette.mode === 'dark' ? "1px solid #6b7280" : "1px solid #e0e0e0",

                          borderRadius: "12px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: "bold",
                            color: wasAttempted
  ? q.is_correct
    ? theme.palette.success.main
    : theme.palette.error.main
  : theme.palette.text.secondary,

                            }}
                          >
                            Your Answer:
                          </Typography>
                          {!wasAttempted && (
                            <Chip
                              label="Not Attempted"
                              size="small"
                              sx={{
                                bgcolor: "#9e9e9e",
                                color: "white",
                                fontSize: "0.75rem",
                                borderRadius: "8px",
                              }}
                            />
                          )}
                        </Box>
                       <Typography
  component="div"
  sx={{
    fontSize: "1rem",
    color: wasAttempted
  ? q.is_correct
    ? theme.palette.success.main
    : theme.palette.error.main
  : theme.palette.text.secondary,

    fontWeight: wasAttempted ? "500" : "normal",
    fontStyle: !wasAttempted ? "italic" : "normal",
    "& > div": {
      display: "inline",
    },
  }}
>
  {renderContent(parseGivenAnswer(q.given_answer))}
</Typography>
                      </Paper>
                      {/* Correct Answer */}
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                         bgcolor: theme.palette.mode === 'dark' ? '#1e3a2f' : "#e8f5e9",
border: "1px solid #4caf50",

                          borderRadius: "12px",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: "bold",
                            color: "#151313ff",
                            mb: 1,
                          }}
                        >
                          Correct Answer:
                        </Typography>
                      <Typography
  component="div"
  sx={{
    fontSize: "1rem",
    color: "#151313ff",
    fontWeight: "500",
    "& > div": {
      display: "inline",
    },
  }}
>
  {Array.isArray(q.correct_answer)
    ? q.correct_answer.map((ans, i) => (
        <span key={i}>
          {renderContent(ans)}
          {i < q.correct_answer.length - 1 ? ", " : ""}
        </span>
      ))
    : renderContent(q.correct_answer)}
</Typography>
                      </Paper>
                      {/* Explanation (always shown if available) */}
                      {/* Explanation (always shown if available) */}
                      {(q.explanation || getExplanationForAnswer(q)) && (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            mt: 2,
                           bgcolor: theme.palette.mode === 'dark' ? '#1e2a47' : "#e3f2fd",
border: "1px solid #2196f3",

                            borderRadius: "12px",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: "bold",
                              color: "#1565c0",
                              mb: 1,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                            Explanation:
                          </Typography>
                         <Typography
  component="div"
  sx={{
    fontSize: "0.95rem",
    color: "#1565c0",
    lineHeight: 1.6,
    "& > div": {
      display: "inline",
    },
  }}
>
  {renderContent(getExplanationForAnswer(q) || q.explanation || "No explanation provided.")}
</Typography>

                        </Paper>
                      )}
                    </Box>
                  </Paper>
                );
              })
            ) : (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Typography variant="h6" color="text.secondary">
                  No review data available
                </Typography>
              </Box>
            )}
          </Box>
          {/* Modal Footer */}
          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.background.paper,
              borderTop: "1px solid #e0e0e0",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              onClick={() => setReviewModalOpen(false)}
              sx={{
                minWidth: 120,
                borderRadius: "12px",
                padding: "10px 24px",
                fontWeight: "600",
                textTransform: "none",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                },
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal
        open={zoomModalOpen}
        onClose={closeZoomModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(4px)",
        }}
      >
        <Box
          sx={{
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "auto",
            outline: "none",
            p: 2,
          }}
        >
          <img
            src={zoomImage}
            alt="Zoomed question"
            style={{
              maxWidth: "100%",
              maxHeight: "80vh",
              borderRadius: "8px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            }}
          />
        </Box>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentPracticePage;
